/**
 * Tiny external store shared by the Tasks action button and the side-rail
 * panel (same pattern as the findings-refresh store). The panel-state hook is
 * the single poller: it publishes counts + extraction status here after every
 * fetch; the button hook renders from the snapshot without fetching anything,
 * so the two can never disagree. The button's click toggles `collapsed`, which
 * the panel obeys.
 */

/** Lifecycle of the extraction run, read off the job marker Task.
 *  `unknown` = no marker (never enqueued, pre-marker visit, or unreadable). */
export type ClinicalTasksExtraction =
  | 'unknown'
  | 'generating'
  | 'done'
  | 'failed';

export interface TasksPanelSnapshot {
  readonly appointmentId: string | null;
  /** Panel hidden by the user via the Tasks button. */
  readonly collapsed: boolean;
  /** null until the first fetch for this appointment lands. */
  readonly counts: {
    readonly pending: number;
    readonly accepted: number;
  } | null;
  readonly extraction: ClinicalTasksExtraction;
}

const INITIAL: TasksPanelSnapshot = {
  appointmentId: null,
  collapsed: false,
  counts: null,
  extraction: 'unknown',
};

let snapshot: TasksPanelSnapshot = INITIAL;
const listeners = new Set<() => void>();

const emit = () => {
  for (const listener of listeners) listener();
};

export const subscribeTasksPanel = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export const getTasksPanelSnapshot = (): TasksPanelSnapshot => snapshot;

/** The Tasks button's click — show/hide the side-rail panel. */
export function toggleTasksPanel(): void {
  snapshot = { ...snapshot, collapsed: !snapshot.collapsed };
  emit();
}

/**
 * Published by the panel-state hook after every fetch. New appointment:
 * expanded with tasks, collapsed without (0-pill on the Tasks button is the
 * way in). Tasks arriving on a zero-task session re-expand ("auto-open for
 * review" — covers extraction finishing, fast runs, regeneration while
 * collapsed).
 */
export function publishTasksPanel(
  appointmentId: string,
  next: {
    pending: number;
    accepted: number;
    extraction: ClinicalTasksExtraction;
  },
): void {
  const sameAppointment = snapshot.appointmentId === appointmentId;
  const prevTotal = snapshot.counts
    ? snapshot.counts.pending + snapshot.counts.accepted
    : 0;
  const nextTotal = next.pending + next.accepted;
  const tasksArrived = sameAppointment && prevTotal === 0 && nextTotal > 0;
  const collapsed = sameAppointment
    ? !tasksArrived && snapshot.collapsed
    : nextTotal === 0;
  if (
    sameAppointment &&
    collapsed === snapshot.collapsed &&
    snapshot.counts?.pending === next.pending &&
    snapshot.counts?.accepted === next.accepted &&
    snapshot.extraction === next.extraction
  ) {
    // Steady-state poll tick — keep the snapshot reference stable so
    // useSyncExternalStore subscribers don't re-render.
    return;
  }
  snapshot = {
    appointmentId,
    collapsed,
    counts: { pending: next.pending, accepted: next.accepted },
    extraction: next.extraction,
  };
  emit();
}

/** Test-only: back to the pristine snapshot. */
export function resetTasksPanelStore(): void {
  snapshot = INITIAL;
  emit();
}
